import { createContext, useContext } from 'react';
import { getMeControllerGetMeQueryKey, useMeControllerGetMe } from '@/generated/api/endpoints/identity/identity';
import type { AttributeGroupDto, MeTenantDto, MeUserDto } from '@/generated/api/models';
import { useAuth } from '@/lib/auth/use-auth';

interface MeContextValue {
  user: MeUserDto | null;
  tenant: MeTenantDto | null;
  permissions: string[];
  registry: AttributeGroupDto[];
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, currentTenant } = useAuth();

  // `/me` is scoped to the caller (session cookie) and the tenant addressed by
  // the subdomain (server-side); it only makes sense once both hold. Keyed by
  // the current tenant so it refetches when the user navigates to another
  // tenant's subdomain.
  const { data } = useMeControllerGetMe({
    query: {
      queryKey: [...getMeControllerGetMeQueryKey(), currentTenant?.id],
      enabled: isAuthenticated && currentTenant !== null,
      // Opts out of the global throw-on-403: this provider renders above the
      // RouterProvider, so no route boundary could catch it and the whole app
      // would go down. A denied /me is the auth state machine's business
      // (`frontendUserState` → the `_auth` state screens), not this screen's.
      throwOnError: false,
    },
  });

  const me = data?.data ?? null;

  const value: MeContextValue = {
    user: me?.user ?? null,
    tenant: me?.tenant ?? null,
    permissions: me?.permissions ?? [],
    registry: me?.registry ?? [],
  };

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error('useMe must be used within MeProvider');
  }
  return ctx;
}
